(() => {
  if (window.panteraAjaxCartReady) return;
  window.panteraAjaxCartReady = true;

  let sectionRendererPromise;
  let confirmedCartCount;
  const pendingAdditions = new Map();

  const ensureToast = () => {
    let toast = document.querySelector('[data-pantera-cart-toast]');
    if (toast) return toast;
    toast = document.createElement('div');
    toast.setAttribute('data-pantera-cart-toast', '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = '<strong>Producto agregado</strong><span>Tu carrito se actualizó correctamente.</span><a href="/cart">Ver carrito</a>';
    document.body.appendChild(toast);
    return toast;
  };

  const showToast = (title, message) => {
    const toast = ensureToast();
    const strong = toast.querySelector('strong');
    const span = toast.querySelector('span');
    if (strong) strong.textContent = title || 'Carrito actualizado';
    if (span) span.textContent = message || 'Tu carrito se actualizó correctamente.';
    toast.classList.add('is-visible');
    window.clearTimeout(window.panteraCartToastTimer);
    window.panteraCartToastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3600);
  };

  const readCartCount = () => {
    const countNode = document.querySelector('[ref="cartBubbleCount"], .cart-bubble__text-count, [data-cart-count]');
    const visibleText = countNode?.textContent?.trim();
    const visibleCount = Number(visibleText);
    if (visibleText && Number.isFinite(visibleCount)) return Math.max(0, visibleCount);

    try {
      const stored = JSON.parse(sessionStorage.getItem('cart-count') || 'null');
      const storedCount = Number(stored?.value);
      return Number.isFinite(storedCount) ? Math.max(0, storedCount) : 0;
    } catch (_) {
      return 0;
    }
  };

  const getPendingAdditionCount = () => [...pendingAdditions.values()].reduce((total, operation) => total + operation.quantity, 0);

  const renderCartCount = (count) => {
    const normalizedCount = Math.max(0, Number(count) || 0);
    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(normalizedCount), timestamp: Date.now() }));
    } catch (_) {
      // The visual count should still update when storage is unavailable.
    }
    document.querySelectorAll('[ref="cartBubbleCount"], .cart-bubble__text-count, [data-cart-count]').forEach((node) => {
      node.textContent = String(normalizedCount);
      node.classList.toggle('hidden', normalizedCount === 0);
    });
    document.querySelectorAll('[ref="cartBubble"], .cart-bubble').forEach((node) => node.classList.toggle('visually-hidden', normalizedCount === 0));
  };

  const renderOptimisticCartCount = () => {
    if (typeof confirmedCartCount !== 'number') confirmedCartCount = readCartCount();
    renderCartCount(confirmedCartCount + getPendingAdditionCount());
  };

  const applyCartCount = (cart) => {
    confirmedCartCount = Math.max(0, Number(cart.item_count) || 0);
    renderOptimisticCartCount();
  };

  const storeCartFeedback = (title, message) => {
    try {
      sessionStorage.setItem('pantera-cart-feedback', JSON.stringify({ title, message }));
    } catch (_) {
      // Feedback is non-critical if storage is unavailable.
    }
  };

  const consumeCartFeedback = () => {
    let feedback;
    try {
      const storedFeedback = sessionStorage.getItem('pantera-cart-feedback');
      if (!storedFeedback) return;
      sessionStorage.removeItem('pantera-cart-feedback');
      feedback = JSON.parse(storedFeedback);
    } catch (_) {
      return;
    }

    if (feedback?.title && feedback?.message) showToast(feedback.title, feedback.message);
  };

  const isPanteraAddForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    const action = form.getAttribute('action') || '';
    return action.includes('/cart/add') && Boolean(form.closest('.pantera-showcase, .pantera-product-row, .pantera-collection-view, .pantera-search-results, .pantera-product-detail'));
  };

  const getSectionRenderer = async () => {
    if (!sectionRendererPromise) {
      sectionRendererPromise = import('@theme/section-renderer').then(({ sectionRenderer }) => sectionRenderer);
    }

    return sectionRendererPromise;
  };

  const refreshCartDrawerSection = async () => {
    const drawer = document.getElementById('cart-drawer');
    const drawerSection = document.getElementById('shopify-section-cart-drawer-section');
    if (!drawer || !drawerSection) return;

    const sectionRenderer = await getSectionRenderer();
    if (!sectionRenderer?.renderSection) return;

    await sectionRenderer.renderSection('cart-drawer-section', {
      cache: false,
      mode: 'hydration',
    });
  };

  const reconcileCartAfterOptimisticAdd = async () => {
    if ([...pendingAdditions.values()].some((operation) => !operation.settled)) {
      renderOptimisticCartCount();
      return;
    }

    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('No se pudo sincronizar el carrito');
      const cart = await response.json();
      confirmedCartCount = Math.max(0, Number(cart.item_count) || 0);
      pendingAdditions.clear();
      renderCartCount(confirmedCartCount);
      await refreshCartDrawerSection().catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pantera-ajax-cart] Drawer refresh failed:', error);
      });
    } catch (error) {
      renderOptimisticCartCount();
      console.warn('[pantera-ajax-cart] Cart reconciliation failed:', error);
    }
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!isPanteraAddForm(form)) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const original = button ? button.textContent : '';
    const isCompactShowcaseButton = button?.matches('.pantera-showcase__add') ?? false;
    const originalAriaLabel = button?.getAttribute('aria-label');
    const formData = new FormData(form);
    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
    const operationId = Date.now() + '-' + Math.random().toString(36).slice(2);
    pendingAdditions.set(operationId, { quantity, settled: false });
    renderOptimisticCartCount();
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (isCompactShowcaseButton) {
        button.textContent = '✓';
        button.classList.add('is-optimistic');
        button.setAttribute('aria-label', 'Producto agregado');
      } else {
        button.textContent = 'Agregado';
      }
    }
    showToast('Producto agregado', 'Puedes seguir comprando o revisar el carrito cuando quieras.');
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!response.ok) throw new Error('No se pudo agregar al carrito');
      await response.json();
      const operation = pendingAdditions.get(operationId);
      if (operation) operation.settled = true;
      renderOptimisticCartCount();
      void reconcileCartAfterOptimisticAdd();
    } catch (error) {
      pendingAdditions.delete(operationId);
      renderOptimisticCartCount();
      showToast('No se pudo agregar', 'Intenta de nuevo.');
      void reconcileCartAfterOptimisticAdd();
      if (button) {
        if (!isCompactShowcaseButton) button.textContent = original || 'Agregar';
        if (originalAriaLabel) {
          button.setAttribute('aria-label', originalAriaLabel);
        } else {
          button.removeAttribute('aria-label');
        }
      }
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.classList.remove('is-optimistic');
        if (originalAriaLabel) {
          button.setAttribute('aria-label', originalAriaLabel);
        } else {
          button.removeAttribute('aria-label');
        }
        button.textContent = original || 'Agregar';
      }
    }
  });

  document.addEventListener('click', async (event) => {
    if (!(event.target instanceof Element)) return;

    const clearButton = event.target.closest('[data-pantera-cart-empty]');
    if (clearButton instanceof HTMLButtonElement) {
      event.preventDefault();
      if (clearButton.dataset.clearing === 'true') return;

      const originalText = clearButton.textContent;
      clearButton.dataset.clearing = 'true';
      clearButton.disabled = true;
      clearButton.setAttribute('aria-busy', 'true');
      clearButton.textContent = 'Vaciando...';
      showToast('Vaciando carrito', 'Estamos actualizando tu carrito.');

      try {
        const response = await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('No se pudo vaciar el carrito');

        const cart = await response.json();
        pendingAdditions.clear();
        applyCartCount(cart);
        await refreshCartDrawerSection().catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[pantera-ajax-cart] Drawer refresh failed:', error);
        });
        showToast('Carrito vacío', 'Se eliminaron todos los productos.');
      } catch (error) {
        clearButton.dataset.clearing = 'false';
        clearButton.disabled = false;
        clearButton.removeAttribute('aria-busy');
        clearButton.textContent = originalText || 'Vaciar carrito';
        showToast('No se pudo vaciar', 'Intenta de nuevo.');
        console.warn('[pantera-ajax-cart] Cart clear failed:', error);
      }

      return;
    }

    const removeLink = event.target.closest('[data-pantera-cart-remove]');
    if (!(removeLink instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    if (removeLink.dataset.removing === 'true') return;

    const href = removeLink.getAttribute('href');
    if (!href) return;

    const removeUrl = new URL(href, window.location.origin);
    const lineId = removeUrl.searchParams.get('id');
    if (!lineId) {
      window.location.assign(href);
      return;
    }

    const cartItem = removeLink.closest('.pantera-cart__item');
    const originalText = removeLink.textContent;
    removeLink.dataset.removing = 'true';
    removeLink.setAttribute('aria-busy', 'true');
    removeLink.setAttribute('aria-disabled', 'true');
    removeLink.textContent = 'Eliminando...';
    cartItem?.classList.add('is-removing');
    showToast('Eliminando producto', 'Estamos actualizando tu carrito.');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: lineId, quantity: 0 }),
      });
      if (!response.ok) throw new Error('No se pudo eliminar el producto');

      const cart = await response.json();
      applyCartCount(cart);
      storeCartFeedback('Producto eliminado', 'El producto se eliminó del carrito.');
      window.location.assign('/cart');
    } catch (error) {
      removeLink.dataset.removing = 'false';
      removeLink.removeAttribute('aria-busy');
      removeLink.removeAttribute('aria-disabled');
      removeLink.textContent = originalText || 'Eliminar';
      cartItem?.classList.remove('is-removing');
      showToast('No se pudo eliminar', 'Intenta de nuevo.');
      console.warn('[pantera-ajax-cart] Cart item removal failed:', error);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', consumeCartFeedback, { once: true });
  } else {
    consumeCartFeedback();
  }
})();
